import React from 'react';
import { Box, Paper, Stack, Typography, Divider, Table, TableHead, TableRow,Grid2, TableCell, TableBody} from '@mui/material';
import { ReceiptComponentProps } from './advance.helper';
import { ReportFooterTimestamp } from '../../../utils/reportUtils';
import { formatDateTimeAmPm } from '../../../utils/dateUtils';
import useCurrency from '../../../components/CurrencyProvider';

// Reusable row component for label-value display
const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Grid2 container size={{ xs: 12 }} sx={{ mb: 0.7, alignItems: 'center' }}>
    <Grid2 sx={{ width: 150, minWidth: 90, textAlign: 'left', pr: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: 15, letterSpacing: 0.1 }}>{label}</Typography>
    </Grid2>
    <Grid2 sx={{ flex: 1 }}>
      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600, fontSize: 15, letterSpacing: 0.1 }}>{value ?? '-'}</Typography>
    </Grid2>
  </Grid2>
);

const ReceiptComponent: React.FC<ReceiptComponentProps> = ({ data }) => {
  const { formatCurrency } = useCurrency();
  const tr = data.transaction_summary;
  const ps = data.payment_summary;
  const pkg = data.package_details as Record<string, unknown> | null;
  const packageName = (pkg && (pkg['package_name'] as string | undefined)) || (pkg && (pkg['name'] as string | undefined)) || undefined;

  return (
    <>


      {/* Receipt Title with decorative lines */}
      <Box data-testid="advance-receipt-component">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            my: 3,
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, height: 2, background: 'linear-gradient(to right, transparent, #e0e0e0)' }} />
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 2,
              color: '#333',
            }}
          >
          Advance Receipt
          </Typography>
          <Box sx={{ flex: 1, height: 2, background: 'linear-gradient(to left, transparent, #e0e0e0)' }} />
        </Box>

        {/* Main Card: Two sections side-by-side like the image */}
        <Paper elevation={0} sx={{ px: 4, borderRadius: 3, mb: 5 }}>
       
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} divider={<Divider orientation="vertical" flexItem />}>
            {/* Advance Details */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 700, mb: 2, letterSpacing: 0.2 }}>Advance Details</Typography>
              <Stack spacing={1.0}>
                <InfoRow label={'Receipt No:'} value={data.receipt_no} />
                <InfoRow label={'Advance Type:'} value={data.advance_type || '-'} />
                <InfoRow label={'Source Clinic:'} value={data?.clinic || '-'} />
                {data.package_details && (
                  <InfoRow label={'Package Name:'} value={packageName || '-'} />
                )}
                <InfoRow label={'Advance Against:'} value={data.advance_against || '-'} />
                <InfoRow label={'Remark:'} value={data.remark || '-'} />
                <InfoRow label={'Issued By:'} value={ps?.created_by || '-'} />
              </Stack>
            </Box>

            {/* Transaction Summary */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 700, mb: 2, letterSpacing: 0.2 }}>Transaction Summary</Typography>
              <Stack spacing={1.0}>
                <InfoRow label={'Date and Time:'} value={formatDateTimeAmPm(data.transaction_date)} />
                <InfoRow label={'Total Amount:'} value={formatCurrency(tr.total_amount)} />
                <InfoRow label={'Used Amount:'} value={formatCurrency(tr.used_amount)} />
                <InfoRow label={'Refunded Amount:'} value={formatCurrency(tr.refund_amount)} />
                <InfoRow label={'Balanced Amount:'} value={formatCurrency(tr.balance_amount)} />
              </Stack>
            </Box>
          </Stack>
          <Divider sx={{ my: 4, borderColor: '#1a1a1a' }} />

          {/* Payment Summary */}

          <Box my={3}>
            <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 700, mb: 2, letterSpacing: 0.2 }}>Payment Summary</Typography>
            <Table size="small" sx={{ mb: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Payment Mode</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ps?.payment_details?.map((pd, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{pd.payment_mode_name}</TableCell>
                    <TableCell align="right">{formatCurrency(pd.amount)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Total Received</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(ps?.total_received_amount || 0)}</TableCell>
                </TableRow>

              </TableBody>
            </Table>
          </Box>

          {/* Signature at lower-right below Payment Summary */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 5, py: 4 }}>
            <Box sx={{ width: 280, textAlign: 'center' }}>
              <Box sx={{ borderTop: '2px dotted', borderColor: 'text.secondary', mb: 0.75 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Issuer Signature</Typography>
            </Box>
          </Box>

          <ReportFooterTimestamp />
        </Paper>
      </Box>
      
    </>
  );
};

export default ReceiptComponent;

